import React, { useContext, useEffect } from 'react';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Tooltip } from '@mui/material';
import GameContext from 'contexts/GameContext';
import { useImBackRequest } from 'hooks/useImBack';
import { useOrientation } from 'hooks/useOrientation';
import { useSupportsHover } from 'hooks/useSupportsHover';
import { useTableSelector } from 'hooks/useTableSelector';
import useGameService from 'services/GameService';

import { tableControlButtonStyle, tableControlIconStyle } from './tableControlStyles';

const SitOutButton = () => {
  const game = useTableSelector((table) => table.game);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const tableType = useTableSelector((table) => table.game.tabletype);

  const { sitOut, imBack } = useGameService();
  const requestImBack = useImBackRequest();
  const { isSatOut, setIsSatOut } = useContext(GameContext);
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';
  const supportsHover = useSupportsHover();

  const playerStatus = game[`p${seatId}status`];

  // Routing through the shared request keeps both I'm Back entry points on one
  // rule - the engine's own returnTable gate - instead of the old duplicated
  // `hand > 3` check. TableActions gates this control on `canExitTable`, which
  // is nearly always the send-now path, but not reliably: `canExitTable` is
  // true mid-rotation on a heads-up or all-sat-out table, and an auto-sat-out
  // (6) seat is refused in that window. The shared request holds those.
  const comeBack = () => {
    requestImBack();
  };

  const triggerSatOut = () => {
    if (isSatOut) {
      comeBack();
    } else {
      if (playerStatus === 2) {
        imBack(seatId);
      }
      setIsSatOut(true);
      sitOut();
    }
  };

  useEffect(() => {
    if (+game[`p${seatId}status`] === 0) {
      setIsSatOut(false);
    } else if (+game[`p${seatId}status`] === 1) {
      setIsSatOut(true);
    }
  }, [game[`p${seatId}status`]]);

  // Desktop-only transform hover; touch keeps the first tap (iOS WKWebView).
  const hoverHandlers = supportsHover
    ? {
        onMouseEnter: (e: React.MouseEvent) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1.04)';
        },
        onMouseLeave: (e: React.MouseEvent) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
        }
      }
    : {};

  const iconSize = tableControlIconStyle(isPortrait);

  // Ghost-tooltip fix (matches ExitTableButton): in portrait the button
  // shows the label as visible text so the MUI Tooltip only duplicated it
  // — worse, its touch listener could keep a stale label floating next to
  // the button after a tap on some mobile browsers. Skip the Tooltip
  // wrapper in portrait entirely, and in landscape (icon-only) disable
  // the touch + focus listeners so only hover-on-a-real-pointer invokes it.
  const cashLabel = isSatOut ? "I'm Back" : 'Sit Out';
  const cashButton = (
    <button
      onClick={triggerSatOut}
      aria-label={cashLabel}
      style={tableControlButtonStyle(isPortrait, isSatOut ? 'active' : 'default')}
      {...hoverHandlers}>
      {!isPortrait &&
        (isSatOut ? (
          <PlayCircleOutlineIcon style={iconSize} />
        ) : (
          <PauseCircleOutlineIcon style={iconSize} />
        ))}
      {isPortrait && <span>{cashLabel}</span>}
    </button>
  );

  const imBackButton = (
    <button
      onClick={comeBack}
      aria-label="I'm Back"
      style={tableControlButtonStyle(isPortrait, 'default')}
      {...hoverHandlers}>
      {!isPortrait && <PlayCircleOutlineIcon style={iconSize} />}
      {isPortrait && <span>I&apos;m Back</span>}
    </button>
  );

  return (
    <>
      {tableType === 's' ? (
        isPortrait ? (
          cashButton
        ) : (
          <Tooltip title={cashLabel} disableTouchListener disableFocusListener>
            {cashButton}
          </Tooltip>
        )
      ) : (
        playerStatus === 1 &&
        (isPortrait ? (
          imBackButton
        ) : (
          <Tooltip title="I'm Back" disableTouchListener disableFocusListener>
            {imBackButton}
          </Tooltip>
        ))
      )}
    </>
  );
};

export default SitOutButton;
